import api, {
  type Article,
  type ArticleFilters,
  type DashboardSummary,
  type PaginatedResponse,
  type CameroonSummary,
} from './api';

/**
 * Build query params from ArticleFilters, omitting undefined values.
 */
function buildFilterParams(filters: ArticleFilters): Record<string, string> {
  const params: Record<string, string> = {};

  if (filters.category) params.category = filters.category;
  if (filters.commodity) params.commodity = filters.commodity.toLowerCase().replace(/\s+/g, '-');
  if (filters.country) params.country = filters.country;
  if (filters.company) params.company = filters.company;
  if (filters.source) params.source = filters.source;
  if (filters.researchHouse) params.researchHouse = filters.researchHouse;
  if (filters.publicationType) params.publicationType = filters.publicationType;
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  if (filters.search) params.search = filters.search;
  if (filters.page) params.page = String(filters.page);
  if (filters.limit) params.limit = String(filters.limit);
  if (filters.sortBy) params.sortBy = filters.sortBy;
  if (filters.sortOrder) params.sortOrder = filters.sortOrder;
  if (filters.language) params.language = filters.language;
  if (filters.includeArchive) params.includeArchive = 'true';

  return params;
}

/**
 * Fetch paginated news articles with optional filters.
 */
export async function getNews(
  filters: ArticleFilters = {}
): Promise<PaginatedResponse<Article>> {
  const params = buildFilterParams(filters);
  const response = await api.get<PaginatedResponse<Article>>('/news', { params });
  return response.data;
}

/**
 * Fetch paginated news articles for a specific category.
 */
export async function getNewsByCategory(
  slug: string,
  filters: ArticleFilters = {}
): Promise<PaginatedResponse<Article>> {
  const params = buildFilterParams({ ...filters, category: slug });
  const response = await api.get<PaginatedResponse<Article>>('/news', { params });
  return response.data;
}

/**
 * Fetch the dashboard summary data.
 */
export async function getDashboardSummary(): Promise<DashboardSummary> {
  const response = await api.get<DashboardSummary>('/news/dashboard/summary');
  return response.data;
}

/**
 * Fetch a single article by ID.
 */
export async function getArticleById(id: number): Promise<Article> {
  const response = await api.get<Article>(`/news/${id}`);
  return response.data;
}

/**
 * Export news articles to CSV with the given filters.
 */
export async function exportNewsToCsv(
  filters: ArticleFilters = {}
): Promise<void> {
  const params = buildFilterParams(filters);
  const response = await api.get('/news/export', {
    params,
    responseType: 'blob',
  });

  // Trigger browser download
  const blob = new Blob([response.data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `stratmined-news-export-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Fetch Cameroon intelligence summary.
 */
export async function getCameroonSummary(): Promise<CameroonSummary> {
  const response = await api.get<CameroonSummary>('/news/cameroon/summary');
  return response.data;
}

/**
 * Fetch the latest aggregation run timestamp.
 */
export async function getLastRun(): Promise<{
  lastRun: { completedAt: string; articlesNew: number; articlesFetched: number } | null;
  /** Next scheduled aggregation, derived server-side from AGGREGATION_CRON. */
  nextRunAt?: string | null;
}> {
  const response = await api.get('/news/dashboard/last-run');
  return response.data;
}

// ─── Manual Article Submit ───

export interface ManualArticleInput {
  url: string;
  title: string;
  description?: string;
  sourceName?: string;
  author?: string;
  imageUrl?: string;
  publishedAt?: string;
  language?: 'en' | 'fr';
  categorySlugs: string[];
}

export interface ManualArticleResult {
  id: number;
  title: string;
  categorySlugs: string[];
  significanceScore: number;
  matchedKeywords: string[];
  /** Entities the server extracted from the title and description. */
  tags?: {
    countries: string[];
    commodities: string[];
    companies: string[];
    regionCode: string | null;
  };
  /** Whether this article's domain is already on the Sources tab. */
  source?: { domain: string | null; known: boolean };
}

/**
 * Submit a manual article (admin only).
 */
export async function submitManualArticle(input: ManualArticleInput): Promise<ManualArticleResult> {
  const response = await api.post<ManualArticleResult>('/news/manual', input);
  return response.data;
}

export interface OcrArticleResult {
  title: string;
  description: string;
  sourceName: string | null;
  author: string | null;
  publishedAt: string | null;
  language: 'en' | 'fr';
}

export type OcrMediaType = 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif' | 'application/pdf';

/**
 * Extract article fields from an image or a PDF (admin only).
 * The file is sent as base64 without the data: prefix.
 */
export async function ocrArticleImage(
  imageBase64: string,
  mediaType: OcrMediaType
): Promise<OcrArticleResult> {
  const response = await api.post<OcrArticleResult>('/news/ocr-image', { imageBase64, mediaType });
  return response.data;
}

export interface UploadedArticleImage {
  id: number;
  url: string;
  sortOrder: number;
}

/**
 * Attach one or more images to an existing article. Images are stored in Azure Blob
 * storage and linked to the article via the article_images table.
 */
export async function attachArticleImages(
  articleId: number,
  images: Array<{ base64: string; mediaType: 'image/png' | 'image/jpeg' | 'image/webp' | 'image/gif' }>
): Promise<{ uploaded: UploadedArticleImage[] }> {
  const response = await api.post<{ uploaded: UploadedArticleImage[] }>(
    `/news/${articleId}/images`,
    { images }
  );
  return response.data;
}

export async function listArticleImages(articleId: number): Promise<UploadedArticleImage[]> {
  const response = await api.get<{ images: UploadedArticleImage[] }>(`/news/${articleId}/images`);
  return response.data.images;
}

export interface MarketPulseBullet {
  en: string;
  fr: string;
  url?: string;
  source?: string;
}

export interface MarketPulse {
  bullets: MarketPulseBullet[];
  generated_at: string;
  article_count: number;
}

/** AI summary of the day's market-relevant news (cached server-side ~6h). */
export async function getMarketPulse(refresh = false): Promise<MarketPulse> {
  const response = await api.get<MarketPulse>('/news/market-pulse', {
    params: refresh ? { refresh: 1 } : undefined,
  });
  return response.data;
}

export interface UncategorisedArticle {
  id: number;
  title: string;
  description: string | null;
  sourceName: string | null;
  url: string;
  publishedAt: string;
  significance: number;
  language: string;
  commodities: string[];
  countries: string[];
}

export interface UncategorisedResponse {
  data: UncategorisedArticle[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

/** Admin: list articles with no category assigned. */
export async function getUncategorisedArticles(page = 1): Promise<UncategorisedResponse> {
  const response = await api.get<UncategorisedResponse>('/news/uncategorised', { params: { page } });
  return response.data;
}

/** Admin: replace an article's category set. */
export async function setArticleCategories(articleId: number, categorySlugs: string[]): Promise<void> {
  await api.patch(`/news/${articleId}/categories`, { categorySlugs });
}

export interface ReportFilterOptions {
  houses: { slug: string; name: string; count: number }[];
  types: { type: string; count: number }[];
}

/** House and publication-type options for the Reports & Studies tab. */
export async function getReportFilters(): Promise<ReportFilterOptions> {
  const { data } = await api.get<ReportFilterOptions>('/news/report-filters');
  return data;
}

export type AsmTheme = 'enforcement' | 'incidents' | 'policy' | 'markets';

export interface AsmBullet {
  en: string;
  fr: string;
  countries: string[];
  commodities: string[];
  url?: string;
  source?: string;
}

export interface AsmDigest {
  days: number;
  sections: { theme: AsmTheme; bullets: AsmBullet[] }[];
  generated_at: string;
  article_count: number;
  language_mix: { language: string; count: number }[];
}

/** Multilingual ASM digest grouped by theme (default 7-day window). */
export async function getAsmDigest(days = 7, refresh = false): Promise<AsmDigest> {
  const params: Record<string, string> = { days: String(days) };
  if (refresh) params.refresh = '1';
  const { data } = await api.get<AsmDigest>('/news/asm-digest', { params, timeout: 120000 });
  return data;
}

export type InfraTheme = 'processing' | 'power' | 'ports' | 'rail' | 'pipelines';

export interface InfraDigest {
  days: number;
  sections: { theme: InfraTheme; bullets: AsmBullet[] }[];
  corridor_watch: { en: string; fr: string } | null;
  generated_at: string;
  article_count: number;
  language_mix: { language: string; count: number }[];
}

/** Infrastructure digest grouped by asset class (default 7-day window). */
export async function getInfraDigest(days = 7, refresh = false): Promise<InfraDigest> {
  const params: Record<string, string> = { days: String(days) };
  if (refresh) params.refresh = '1';
  const { data } = await api.get<InfraDigest>('/news/infra-digest', { params, timeout: 120000 });
  return data;
}

export type TabDigestKind = 'government' | 'companies';
export type TabTheme =
  | 'production' | 'exports' | 'licensing' | 'reserves' | 'transparency'
  | 'projects' | 'exploration' | 'operations' | 'people' | 'strategy';

export interface TabDigest {
  kind: TabDigestKind;
  days: number;
  sections: { theme: TabTheme; bullets: FinanceBullet[] }[];
  highlight: { en: string; fr: string } | null;
  generated_at: string;
  article_count: number;
  language_mix: { language: string; count: number }[];
}

/** Government Data (kind='government') or Mining Companies News ('companies'). */
export async function getTabDigest(kind: TabDigestKind, days = 7, refresh = false): Promise<TabDigest> {
  const params: Record<string, string> = { kind, days: String(days) };
  if (refresh) params.refresh = '1';
  const { data } = await api.get<TabDigest>('/news/tab-digest', { params, timeout: 180000 });
  return data;
}

export type GovTheme = 'codes' | 'permits' | 'fiscal' | 'courts' | 'geopolitics';

export interface GovernanceDigest {
  days: number;
  sections: { theme: GovTheme; bullets: AsmBullet[] }[];
  investor_lens: { en: string; fr: string } | null;
  generated_at: string;
  article_count: number;
  language_mix: { language: string; count: number }[];
}

/** Mining Governance digest grouped by instrument (default 7-day window). */
export async function getGovernanceDigest(days = 7, refresh = false): Promise<GovernanceDigest> {
  const params: Record<string, string> = { days: String(days) };
  if (refresh) params.refresh = '1';
  const { data } = await api.get<GovernanceDigest>('/news/governance-digest', { params, timeout: 180000 });
  return data;
}

export type FinanceKind = 'results' | 'financing';
export type FinanceTheme =
  | 'earnings' | 'guidance' | 'operations' | 'market'
  | 'debt' | 'equity' | 'ma' | 'offtake';

export interface FinanceBullet extends AsmBullet {
  companies: string[];
}

export interface FinanceDigest {
  kind: FinanceKind;
  days: number;
  sections: { theme: FinanceTheme; bullets: FinanceBullet[] }[];
  generated_at: string;
  article_count: number;
  language_mix: { language: string; count: number }[];
}

/** Financial News (kind='results') or Financing (kind='financing') digest. */
export async function getFinanceDigest(kind: FinanceKind, days = 7, refresh = false): Promise<FinanceDigest> {
  const params: Record<string, string> = { kind, days: String(days) };
  if (refresh) params.refresh = '1';
  const { data } = await api.get<FinanceDigest>('/news/finance-digest', { params, timeout: 120000 });
  return data;
}

export type SectorTheme = 'oil-gas' | 'power-renewables' | 'commodities';
export interface SummaryBullet { en: string; fr: string; url?: string; source?: string; }
export interface SummarySection { title_en: string; title_fr: string; bullets: SummaryBullet[]; }
export interface SectorSummary {
  theme: SectorTheme;
  dateFrom: string;
  dateTo: string;
  sections: SummarySection[];
  generated_at: string;
  article_count: number;
}

/** AI summary for a sector theme over a date range (defaults to today). */
export async function getSectorSummary(
  theme: SectorTheme,
  from?: string,
  to?: string,
  refresh = false
): Promise<SectorSummary> {
  const params: Record<string, string> = { theme };
  if (from) params.from = from;
  if (to) params.to = to;
  if (refresh) params.refresh = '1';
  const { data } = await api.get<SectorSummary>('/news/sector-summary', { params });
  return data;
}
